// Read More Component for Collection Descriptions
class ReadMoreElement extends HTMLElement {
	constructor() {
		super();
		this.isExpanded = false;
		this.originalHeight = '';
		// Support both old and new attributes for backwards compatibility
		this.maxLines = parseInt(this.getAttribute('max-lines') || this.getAttribute('collapsed-height') || '4');
		this.enableOnDesktop = this.hasAttribute('enable-desktop') || false;
		this.init();
	}

	init() {
		this.classList.add('read-more-component');
		const content = this.querySelector('.read-more-content');
		if (!content) return;

		// Check if we're on mobile (< 640px)
		const isMobile = () => window.innerWidth < 640;

		// Check if read-more should be active based on screen size and settings
		const shouldBeActive = () => {
			return this.enableOnDesktop || isMobile();
		};

		// Function to calculate line height and number of lines
		const getLineInfo = () => {
			// Temporarily remove height restrictions to get natural content
			const originalHeight = content.style.height;
			const originalOverflow = content.style.overflow;
			content.style.height = 'auto';
			content.style.overflow = 'visible';

			// Calculate line height by measuring a single line
			const tempDiv = document.createElement('div');
			tempDiv.style.cssText = window.getComputedStyle(content).cssText;
			tempDiv.style.height = 'auto';
			tempDiv.style.width = content.offsetWidth + 'px';
			tempDiv.style.position = 'absolute';
			tempDiv.style.visibility = 'hidden';
			tempDiv.style.whiteSpace = 'nowrap';
			tempDiv.textContent = 'M'; // Use a standard character
			document.body.appendChild(tempDiv);
			const lineHeight = tempDiv.offsetHeight;
			document.body.removeChild(tempDiv);

			// Get total content height and calculate lines
			const totalHeight = content.scrollHeight;
			const totalLines = Math.ceil(totalHeight / lineHeight);
			const collapsedHeight = lineHeight * this.maxLines;

			// Restore original styles
			content.style.height = originalHeight;
			content.style.overflow = originalOverflow;

			return {
				lineHeight,
				totalLines,
				collapsedHeight,
				needsTruncation: totalLines > this.maxLines
			};
		};

		// Function to update read-more state
		const updateReadMoreState = () => {
			const lineInfo = getLineInfo();
			const needsReadMore = shouldBeActive() && lineInfo.needsTruncation;
			const hasToggle = !!this.querySelector('.read-more-toggle');

			// Always update truncation class based on content length
			if (lineInfo.needsTruncation) {
				this.classList.add('has-overflow');
			} else {
				this.classList.remove('has-overflow');
			}

			if (needsReadMore && !hasToggle) {
				// Need read-more but don't have it - add it
				this.setupReadMore(content, lineInfo);
				this.classList.add('active', 'truncated');
			} else if (!needsReadMore && hasToggle) {
				// Don't need read-more but have it - remove it
				const toggleButton = this.querySelector('.read-more-toggle');
				if (this.isExpanded) {
					// If expanded, collapse it first before removing
					this.toggleReadMore(content, lineInfo, toggleButton);
				}
				toggleButton.remove();
				content.style.height = 'auto';
				this.classList.remove('expanded', 'active', 'truncated');
			}

			// Update mobile-specific class
			if (needsReadMore && isMobile()) {
				this.classList.add('mobile-active');
			} else {
				this.classList.remove('mobile-active');
			}

			// Update truncated state for active read-more
			if (needsReadMore && hasToggle && !this.isExpanded) {
				this.classList.add('truncated');
			} else {
				this.classList.remove('truncated');
			}

			// If we don't need read-more, make sure classes are clean
			if (!needsReadMore) {
				this.classList.remove('active', 'mobile-active', 'truncated');
			}
		};

		// Initial setup
		updateReadMoreState();

		// Handle resize events with debouncing for performance
		let resizeTimeout;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(updateReadMoreState, 100);
		});
	}

	setupReadMore(content, lineInfo) {
		// Set initial collapsed state based on line height
		content.style.height = lineInfo.collapsedHeight + 'px';
		content.style.overflow = 'hidden';
		content.style.transition = 'height 0.3s ease';

		// Create toggle button
		const toggleButton = document.createElement('button');
		toggleButton.className = 'read-more-toggle';
		toggleButton.textContent = 'Read more';
		toggleButton.setAttribute('type', 'button');

		// Add click handler
		toggleButton.addEventListener('click', () => {
			this.toggleReadMore(content, lineInfo, toggleButton);
		});

		this.appendChild(toggleButton);
	}

	toggleReadMore(content, lineInfo, toggleButton) {
		this.isExpanded = !this.isExpanded;

		if (this.isExpanded) {
			// Re-calculate full height each time in case content has changed
			const tempHeight = content.style.height;
			content.style.height = 'auto';
			const currentFullHeight = content.scrollHeight;
			content.style.height = tempHeight;

			content.style.height = currentFullHeight + 'px';
			toggleButton.textContent = 'Read less';
			this.classList.add('expanded');
			this.classList.remove('truncated'); // Remove gradient overlay when expanded

			// Set height to auto after transition completes so it can respond to resizes
			setTimeout(() => {
				if (this.isExpanded) {
					content.style.height = 'auto';
				}
			}, 300); // Match the CSS transition duration
		} else {
			content.style.height = lineInfo.collapsedHeight + 'px';
			toggleButton.textContent = 'Read more';
			this.classList.remove('expanded');
			this.classList.add('truncated'); // Show gradient overlay when collapsed
		}
	}
}

// Only define the custom element if it hasn't been defined already
if (!customElements.get('read-more')) {
	customElements.define('read-more', ReadMoreElement);
}
